import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilingual } from '../hooks/useBilingual';
import './Header.css';
import { useAlert } from '../contexts/AlertContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tb } = useBilingual();
  const { showInfo } = useAlert();
  const { currentLanguage, changeLanguage, languages, getCurrentLanguageInfo } = useLanguage();
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [studentStats, setStudentStats] = useState(null);
  const desktopLanguageSelectorRef = useRef(null);
  const mobileLanguageSelectorRef = useRef(null);
  const userDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const isActive = (path) => {
    return location.pathname === path ? 'nav-link active' : 'nav-link';
  };

  // 로그인 상태 확인
  const isLoggedIn = !!localStorage.getItem('token');
  const userType = localStorage.getItem('userType');
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;

  // /books 라우트인지 확인
  const isBooksPage = location.pathname === '/books';

  const handleLanguageSelect = (event, language) => {
    event.stopPropagation(); // 이벤트 버블링 방지
    changeLanguage(language.code);
    setIsLangDropdownOpen(false);
    showInfo(`${language.flag} ${language.name}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('user');
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
    showInfo(tb('header.logout'));
    navigate('/');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // 학생 통계 가져오기 (XP, Streak)
  useEffect(() => {
    const fetchStudentStats = async () => {
      if (isLoggedIn && userType === 'student' && user?.id) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/v2/assessments/student/${user.id}/stats`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const stats = await response.json();
            setStudentStats(stats);
          }
        } catch (error) {
          console.error('Failed to fetch student stats:', error);
        }
      }
    };

    fetchStudentStats();
    // 60초마다 업데이트
    const interval = setInterval(fetchStudentStats, 60000);
    
    return () => clearInterval(interval);
  }, [isLoggedIn, userType, user?.id]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopLanguageSelectorRef.current && !desktopLanguageSelectorRef.current.contains(event.target) &&
          mobileLanguageSelectorRef.current && !mobileLanguageSelectorRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="header">
      <div className="header-content">
        <Link to={isBooksPage ? "/books" : "/"} className="logo">
          <span className="logo-text">NEXPA LIDIA</span>
        </Link>
        
        <div className="header-right">
          {/* Desktop Navigation */}
          <nav className="nav desktop-nav">
            {isBooksPage ? (
              // /books 페이지에서는 Books 메뉴만 표시
              <Link to="/books" className={isActive('/books')}>
                Books
              </Link>
            ) : (
              // 다른 페이지에서는 Home, About, Play, Users 메뉴 표시
              <>
                <Link to="/" className={isActive('/')}>
                  {tb('header.home')}
                </Link>
                <Link to="/about" className={isActive('/about')}>
                  {tb('header.about')}
                </Link>
                <Link to="/test" className={isActive('/test')}>
                  {tb('header.play')}
                </Link>
                {isLoggedIn && userType === 'student' && (
                  <Link to="/dashboard" className={isActive('/dashboard')}>
                    {tb('header.dashboard')}
                  </Link>
                )}
                {isLoggedIn && userType === 'student' && (
                  <Link to="/game-progress" className={isActive('/game-progress')}>
                    {tb('header.progress')}
                  </Link>
                )}
                {isLoggedIn && userType === 'student' && (
                  <Link to="/rankings" className={isActive('/rankings')}>
                    {tb('header.rankings')}
                  </Link>
                )}
              </>
            )}
          </nav>
          
          {/* Desktop User Menu */}
          <div className="desktop-user-menu">
            {/* Simplified Language Toggle (데스크톱 공통 노출) */}
            <div className="lang-toggle-container" ref={desktopLanguageSelectorRef}>
              <button 
                className="lang-toggle-btn"
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
              >
                <span className="lang-globe">🌐</span>
              </button>
              {isLangDropdownOpen && (
                <div className="lang-dropdown">
                  {languages.map((language) => (
                    <button 
                      key={language.code}
                      className={language.code === currentLanguage ? 'active' : ''}
                      onClick={(e) => handleLanguageSelect(e, language)}
                    >
                      {language.flag} {language.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isLoggedIn ? (
              <div className="user-section">
                {/* XP and Streak Badges (학생만) */}
                {userType === 'student' && studentStats && (
                  <div className="gamification-stats">
                    <div className="stat-badge xp-badge" title={`Level ${studentStats.current_level}`}>
                      <span className="stat-icon">⚡</span>
                      <span className="stat-value">{studentStats.total_xp}</span>
                      <span className="stat-label">XP</span>
                      <span className="stat-level">Lv.{studentStats.current_level}</span>
                    </div>
                    {studentStats.current_streak > 0 && (
                      <div className="stat-badge streak-badge" title={`${studentStats.current_streak}일 연속 학습`}>
                        <span className="stat-icon">🔥</span>
                        <span className="stat-value">{studentStats.current_streak}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="user-profile" ref={userDropdownRef}>
                  <button 
                    className="user-profile-btn"
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  >
                    <div className="user-avatar">
                      {userType === 'admin' ? '👨‍💼' : '👤'}
                    </div>
                    <div className="user-info">
                      <span className="user-name">
                        {userType === 'admin' ? user?.username : user?.name || 'User'}
                      </span>
                      <span className="user-type">
                        {userType === 'admin' ? 'Admin' : 'Student'}
                      </span>
                    </div>
                    <svg 
                      className={`chevron-icon ${isUserDropdownOpen ? 'open' : ''}`}
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  {isUserDropdownOpen && (
                    <div className="user-dropdown-menu">
                      {userType === 'student' && (
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            setIsUserDropdownOpen(false);
                            navigate('/profile');
                          }}
                        >
                          <span className="item-icon">⚙️</span>
                          {tb('header.profile')}
                        </button>
                      )}
                      {userType === 'admin' && (
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            setIsUserDropdownOpen(false);
                            navigate('/admin');
                          }}
                        >
                          <span className="item-icon">🛠️</span>
                          {tb('header.admin')}
                        </button>
                      )}
                      <div className="dropdown-divider"></div>
                      <button
                        className="dropdown-item logout-item"
                        onClick={handleLogout}
                      >
                        <span className="item-icon">🚪</span>
                        {tb('header.logout')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="auth-section">
                <Link to="/login" className="auth-btn login-btn">
                  {tb('header.login')}
                </Link>
                <Link to="/register" className="auth-btn register-btn">
                  {tb('header.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Language Toggle */}
          <div className="mobile-lang-toggle-container" ref={mobileLanguageSelectorRef}>
            <button 
              className="mobile-lang-toggle-btn"
              onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
            >
              <span className="lang-globe">🌐</span>
            </button>
            {isLangDropdownOpen && (
              <div className="mobile-lang-dropdown">
                {languages.map((language) => (
                  <button 
                    key={language.code}
                    className={language.code === currentLanguage ? 'active' : ''}
                    onClick={(e) => handleLanguageSelect(e, language)}
                  >
                    {language.flag} {language.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="mobile-menu-container" ref={mobileMenuRef}>
            <button 
              className="hamburger-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            </button>

            {isMobileMenuOpen && (
              <div className="mobile-menu">
                <div className="mobile-menu-content">
                  {/* Navigation Links */}
                  <div className="mobile-nav">
                    {isBooksPage ? (
                      <Link 
                        to="/books" 
                        className={isActive('/books')}
                        onClick={closeMobileMenu}
                      >
                        Books
                      </Link>
                    ) : (
                      <>
                        <Link 
                          to="/" 
                          className={isActive('/')}
                          onClick={closeMobileMenu}
                        >
                          {tb('header.home')}
                        </Link>
                        <Link 
                          to="/about" 
                          className={isActive('/about')}
                          onClick={closeMobileMenu}
                        >
                          {tb('header.about')}
                        </Link>
                        <Link 
                          to="/test" 
                          className={isActive('/test')}
                          onClick={closeMobileMenu}
                        >
                          {tb('header.play')}
                        </Link>
                        {isLoggedIn && userType === 'student' && (
                          <Link 
                            to="/dashboard" 
                            className={isActive('/dashboard')}
                            onClick={closeMobileMenu}
                          >
                            {tb('header.dashboard')}
                          </Link>
                        )}
                        {isLoggedIn && userType === 'student' && (
                          <Link 
                            to="/game-progress" 
                            className={isActive('/game-progress')}
                            onClick={closeMobileMenu}
                          >
                            {tb('header.progress')}
                          </Link>
                        )}
                        {isLoggedIn && userType === 'student' && (
                          <Link 
                            to="/rankings" 
                            className={isActive('/rankings')}
                            onClick={closeMobileMenu}
                          >
                            {tb('header.rankings')}
                          </Link>
                        )}
                      </>
                    )}
                  </div>

                  {/* User Section */}
                  <div className="mobile-user-section">
                    {isLoggedIn ? (
                      <div className="mobile-user-info">
                        <div className="mobile-user-header">
                          <span className="mobile-user-icon">
                            {userType === 'admin' ? '🛠️' : '👤'}
                          </span>
                          <span 
                            className="mobile-user-name"
                            onClick={() => {
                              if (userType === 'student') {
                                closeMobileMenu();
                                navigate('/profile');
                              }
                            }}
                            style={{
                              cursor: userType === 'student' ? 'pointer' : 'default',
                              textDecoration: userType === 'student' ? 'underline' : 'none'
                            }}
                          >
                            {userType === 'admin' ? user?.username : user?.name || 'User'}
                          </span>
                        </div>
                        
                        {userType === 'admin' && (
                          <button
                            className="mobile-menu-button"
                            onClick={() => {
                              closeMobileMenu();
                              navigate('/admin');
                            }}
                          >
                            {tb('header.admin')}
                          </button>
                        )}
                        
                        <button
                          className="mobile-menu-button logout"
                          onClick={handleLogout}
                        >
                          {tb('header.logout')}
                        </button>
                      </div>
                    ) : (
                      <div className="mobile-auth-buttons">
                        <Link 
                          to="/login" 
                          className="mobile-btn mobile-btn-primary"
                          onClick={closeMobileMenu}
                        >
                          {tb('header.login')}
                        </Link>
                        <Link 
                          to="/register" 
                          className="mobile-btn mobile-btn-secondary"
                          onClick={closeMobileMenu}
                        >
                          {tb('header.register')}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 